import {useState} from 'react';

import i18n from '@dms/i18n';

import {parseDate, useInterval} from 'shared/web';

const SECOND_MS = 1000;
const HOUR_MS = 60 * 60 * SECOND_MS;
const SECONDS_IN_MINUTE = 60;
const SECONDS_IN_HOUR = 3600;
const SECONDS_IN_DAY = 86400;

/** Auctions ending within this threshold are considered "ending soon". */
const ENDING_SOON_THRESHOLD_MS = HOUR_MS;

function computeSegments(diffMs: number) {
  const totalSeconds = Math.max(0, Math.floor(diffMs / SECOND_MS));
  return {
    days: Math.floor(totalSeconds / SECONDS_IN_DAY),
    hours: Math.floor((totalSeconds % SECONDS_IN_DAY) / SECONDS_IN_HOUR),
    minutes: Math.floor((totalSeconds % SECONDS_IN_HOUR) / SECONDS_IN_MINUTE),
    seconds: totalSeconds % SECONDS_IN_MINUTE,
  };
}

function formatCountdown(segments: {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}): string {
  if (segments.days > 0) {
    return `${segments.days}d ${segments.hours}h`;
  }
  if (segments.hours > 0) {
    return `${segments.hours}h ${segments.minutes}m`;
  }
  if (segments.minutes > 0) {
    return `${segments.minutes}m ${segments.seconds}s`;
  }
  return `${segments.seconds}s`;
}

export interface AuctionCountdownState {
  label: string;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  /** True when the auction status is ended. */
  isEnded: boolean;
  /**
   * True when less than 1 hour remains, including an already elapsed end time — late bids can
   * extend it, so an elapsed countdown on a still-active auction stays "ending soon".
   */
  isEndingSoon: boolean;
  /** True when a valid end time is present (for display). */
  hasEndTime: boolean;
  /**
   * True when the end time has already passed while the status is still active — late bids
   * extended the lot and the listed end time is stale. Never true for an ended auction, so it
   * distinguishes "still running, listed time is out of date" from "really over".
   */
  hasElapsed: boolean;
}

function hasValidEndTime(endTime: string | null | undefined): boolean {
  return getParsedEndTime(endTime) !== null;
}

function getParsedEndTime(endTime: string | null | undefined): Date | null {
  if (!endTime) {
    return null;
  }

  const parsed = parseDate(endTime);
  if (parsed === null || isNaN(parsed.getTime())) {
    return null;
  }

  return parsed;
}

function createEndedState(hasEndTime: boolean): AuctionCountdownState {
  return {
    label: i18n.t('general.labels.closed'),
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    isEnded: true,
    isEndingSoon: false,
    hasEndTime,
    hasElapsed: false,
  };
}

function createOpenEndedState(): AuctionCountdownState {
  return {
    label: '',
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    isEnded: false,
    isEndingSoon: false,
    hasEndTime: false,
    hasElapsed: false,
  };
}

/**
 * Stops at zero — further ticks recompute the same `0s`. Ended is never derived from the countdown,
 * only from a refreshed `isEnded` (`carStatus`), because late bids can push `endTime` back. A
 * countdown running out in an open view therefore sits at `0s` until the next fetch — accepted.
 */
function shouldTickCountdown(state: AuctionCountdownState): boolean {
  if (state.isEnded || !state.hasEndTime) {
    return false;
  }

  return state.days > 0 || state.hours > 0 || state.minutes > 0 || state.seconds > 0;
}

function computeState(endTime: string | null | undefined, isEnded: boolean): AuctionCountdownState {
  const endTimeIsValid = hasValidEndTime(endTime);

  if (isEnded) {
    return createEndedState(endTimeIsValid);
  }

  if (!endTimeIsValid) {
    return createOpenEndedState();
  }

  const parsed = getParsedEndTime(endTime);
  if (parsed === null) {
    return createOpenEndedState();
  }

  const diff = parsed.getTime() - Date.now(); // eslint-disable-line eag/no-native-date-math -- Pre-existing native Date math, migrate to date-fns when possible
  const segments = computeSegments(diff);
  return {
    label: formatCountdown(segments),
    ...segments,
    isEnded: false,
    isEndingSoon: diff < ENDING_SOON_THRESHOLD_MS,
    hasEndTime: true,
    hasElapsed: diff <= 0,
  };
}

/**
 * Returns a live countdown state for the given ISO 8601 end time.
 * Updates every second while the auction is still live and has an end time.
 *
 * Auction end is determined solely by `isEnded` (from auction status), never by
 * whether `endTime` has passed — `endTime` may be null for open-ended auctions.
 *
 * - `isEnded`: true when auction status is ended.
 * - `isEndingSoon`: true when less than 1 hour remains, or the end time already elapsed.
 * - `hasElapsed`: true when the end time elapsed but the status is still active (extended lot).
 * - `label`: human-readable countdown string (e.g. "2h 5m", "45m 30s", "30s").
 */
export function useAuctionCountdown(
  endTime: string | null | undefined,
  isEnded: boolean
): AuctionCountdownState {
  const [prevEndTime, setPrevEndTime] = useState(endTime);
  const [prevIsEnded, setPrevIsEnded] = useState(isEnded);
  const [state, setState] = useState<AuctionCountdownState>(() => computeState(endTime, isEnded));

  if (endTime !== prevEndTime || isEnded !== prevIsEnded) {
    setPrevEndTime(endTime);
    setPrevIsEnded(isEnded);
    setState(computeState(endTime, isEnded));
  }

  useInterval(
    () => {
      setState(computeState(endTime, isEnded));
    },
    shouldTickCountdown(state) ? SECOND_MS : null
  );

  return state;
}
